import React, { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import axiosInstance from '../config/axios.config';

export default function ApplicantType() {
    const [applicantTypes, setApplicantTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [newTypeDialog, setNewTypeDialog] = useState(false);
    const [editTypeDialog, setEditTypeDialog] = useState(false);
    const [viewTypeDialog, setViewTypeDialog] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [newType, setNewType] = useState({ applicant_name: '', code: '' });
    const [globalFilter, setGlobalFilter] = useState('');
    const toast = useRef(null);
    const dt = useRef(null);

    // Fetch all applicant types
    const loadApplicantTypes = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/applicants/types');
            
            console.log('API Response:', response.data);
            
            if (response.data.success) {
                const dataToSet = response.data.data;
                
                // Ensure data is always an array
                if (Array.isArray(dataToSet)) {
                    setApplicantTypes(dataToSet);
                } else if (dataToSet && Array.isArray(dataToSet.data)) {
                    // Handle nested data structure
                    setApplicantTypes(dataToSet.data);
                } else if (dataToSet && typeof dataToSet === 'object') {
                    // If it's an object, convert to array
                    setApplicantTypes([dataToSet]);
                } else {
                    setApplicantTypes([]);
                }
            } else {
                setApplicantTypes([]);
            }
        } catch (error) {
            console.error('Error loading applicant types:', error);
            setApplicantTypes([]); // Ensure array on error
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to load applicant types',
                life: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadApplicantTypes();
    }, []);

    // ✅ Create new applicant type
    const createApplicantType = async () => {
        if (!newType.applicant_name.trim()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Applicant name is required',
                life: 3000
            });
            return;
        }

        if (!newType.code.trim()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Code is required',
                life: 3000
            });
            return;
        }

        try {
            const response = await axiosInstance.post('/applicants/types', newType);
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Created',
                    detail: 'Applicant type added successfully',
                    life: 3000
                });
                setNewTypeDialog(false);
                setNewType({ applicant_name: '', code: '', description: '' });
                loadApplicantTypes();
            }
        } catch (error) {
            console.error('Error creating applicant type:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to create applicant type',
                life: 3000
            });
        }
    };

    // ✅ Open view dialog
    const openViewDialog = (type) => {
        setSelectedType({ ...type });
        setViewTypeDialog(true);
    };

    // ✅ Open edit dialog
    const openEditDialog = (type) => {
        setSelectedType({ ...type });
        setEditTypeDialog(true);
    };

    // ✅ Update applicant type
    const updateApplicantType = async () => {
        if (!selectedType.applicant_name.trim()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Applicant name is required',
                life: 3000
            });
            return;
        }

        if (!selectedType.code.trim()) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Code is required',
                life: 3000
            });
            return;
        }

        try {
            const response = await axiosInstance.put(`/applicants/types/${selectedType.id}`, selectedType);
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Updated',
                    detail: 'Applicant type updated successfully',
                    life: 3000
                });
                setEditTypeDialog(false);
                setSelectedType(null);
                loadApplicantTypes();
            }
        } catch (error) {
            console.error('Error updating applicant type:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to update applicant type',
                life: 3000
            });
        }
    };

    // ✅ Delete single applicant type
    const deleteApplicantType = async (id) => {
        if (!window.confirm('Are you sure you want to delete this applicant type?')) return;

        try {
            const response = await axiosInstance.delete(`/applicants/types/${id}`);
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Deleted',
                    detail: 'Applicant type deleted successfully',
                    life: 3000
                });
                loadApplicantTypes();
            }
        } catch (error) {
            console.error('Error deleting applicant type:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to delete applicant type',
                life: 3000
            });
        }
    };

    // ✅ Bulk delete applicant types
    const bulkDeleteApplicantTypes = async () => {
        if (selectedTypes.length === 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please select applicant types to delete',
                life: 3000
            });
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${selectedTypes.length} applicant type(s)?`)) return;

        try {
            const ids = selectedTypes.map(type => type.id);
            const response = await axiosInstance.delete('/applicants/types', {
                data: { ids }
            });
            if (response.data.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Deleted',
                    detail: `${selectedTypes.length} applicant type(s) deleted successfully`,
                    life: 3000
                });
                setSelectedTypes([]);
                loadApplicantTypes();
            }
        } catch (error) {
            console.error('Error bulk deleting applicant types:', error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to delete applicant types',
                life: 3000
            });
        }
    };

    // ✅ Action buttons
    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button 
                icon="pi pi-eye" 
                className="p-button-rounded p-button-info p-button-text" 
                onClick={() => openViewDialog(rowData)}
                tooltip="View"
            />
            <Button 
                icon="pi pi-pencil" 
                className="p-button-rounded p-button-success p-button-text" 
                onClick={() => openEditDialog(rowData)}
                tooltip="Edit"
            />
            <Button 
                icon="pi pi-trash" 
                className="p-button-rounded p-button-danger p-button-text" 
                onClick={() => deleteApplicantType(rowData.id)}
                tooltip="Delete"
            />
        </div>
    );

    // ✅ Toolbar template
    const leftToolbarTemplate = () => (
        <div className="flex gap-2">
            <Button 
                label="New Applicant Type" 
                icon="pi pi-plus" 
                className="p-button-success" 
                onClick={() => setNewTypeDialog(true)} 
            />
            <Button 
                label="Delete Selected" 
                icon="pi pi-trash" 
                className="p-button-danger" 
                onClick={bulkDeleteApplicantTypes}
                disabled={!selectedTypes || selectedTypes.length === 0}
            />
        </div>
    );

    const rightToolbarTemplate = () => (
        <Button 
            label="Refresh" 
            icon="pi pi-refresh" 
            className="p-button-help" 
            onClick={loadApplicantTypes} 
        />
    );

    // ✅ Table header
    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between">
            <h1 className="m-0">Applicant Type Management</h1>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText 
                    type="search" 
                    onInput={(e) => setGlobalFilter(e.target.value)} 
                    placeholder="Search..." 
                />
            </span>
        </div>
    );

    // ✅ Dialog footers
    const createFooter = (
        <div>
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                className="p-button-text" 
                onClick={() => setNewTypeDialog(false)} 
            />
            <Button 
                label="Create" 
                icon="pi pi-check" 
                className="p-button-text" 
                onClick={createApplicantType} 
            />
        </div>
    );

    const editFooter = (
        <div>
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                className="p-button-text" 
                onClick={() => setEditTypeDialog(false)} 
            />
            <Button 
                label="Update" 
                icon="pi pi-check" 
                className="p-button-text" 
                onClick={updateApplicantType} 
            />
        </div>
    );

    return (
        <div className="grid">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />

                    <Toolbar 
                        className="mb-4" 
                        left={leftToolbarTemplate} 
                        right={rightToolbarTemplate}
                    />

                    <DataTable
                        ref={dt}
                        value={applicantTypes}
                        selection={selectedTypes}
                        onSelectionChange={(e) => setSelectedTypes(e.value)}
                        loading={loading}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        dataKey="id"
                        responsiveLayout="scroll"
                        emptyMessage="No applicant types found"
                        globalFilter={globalFilter}
                        header={header}
                        className="p-datatable-gridlines"
                    >
                        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                        <Column field="id" header="ID" sortable style={{ width: '10%' }} />
                        <Column field="applicant_name" header="Applicant Name" sortable style={{ width: '30%' }} />
                        <Column field="code" header="Code" sortable style={{ width: '20%' }} />
                        <Column field="description" header="Description" style={{ width: '30%' }} />
                        <Column header="Actions" body={actionBodyTemplate} style={{ width: '10%' }} />
                    </DataTable>

                    {/* Create New Type Dialog */}
                    <Dialog
                        visible={newTypeDialog}
                        style={{ width: '500px' }}
                        header="Create New Applicant Type"
                        modal
                        className="p-fluid"
                        footer={createFooter}
                        onHide={() => setNewTypeDialog(false)}
                    >
                        <div className="field">
                            <label htmlFor="applicant_name">Applicant Name *</label>
                            <InputText
                                id="applicant_name"
                                value={newType.applicant_name}
                                onChange={(e) => setNewType({ ...newType, applicant_name: e.target.value })}
                                placeholder="Enter applicant name"
                                autoFocus
                            />
                        </div>
                        <div className="field">
                            <label htmlFor="code">Code *</label>
                            <InputText
                                id="code"
                                value={newType.code}
                                onChange={(e) => setNewType({ ...newType, code: e.target.value })}
                                placeholder="Enter code (e.g., SALARIED)"
                            />
                        </div>
                        <div className="field">
                            <label htmlFor="description">Description</label>
                            <InputText
                                id="description"
                                value={newType.description || ''}
                                onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                                placeholder="Enter description (optional)"
                            />
                        </div>
                    </Dialog>

                    {/* View Type Dialog */}
                    <Dialog
                        visible={viewTypeDialog}
                        style={{ width: '600px' }}
                        header="Applicant Type Details"
                        modal
                        onHide={() => setViewTypeDialog(false)}
                        footer={
                            <div>
                                <Button 
                                    label="Close" 
                                    icon="pi pi-times" 
                                    className="p-button-text" 
                                    onClick={() => setViewTypeDialog(false)} 
                                />
                                <Button 
                                    label="Edit" 
                                    icon="pi pi-pencil" 
                                    className="p-button-text" 
                                    onClick={() => {
                                        setViewTypeDialog(false);
                                        openEditDialog(selectedType);
                                    }} 
                                />
                            </div>
                        }
                    >
                        {selectedType && (
                            <div className="grid">
                                <div className="col-12">
                                    <div className="surface-card shadow-2 border-round p-4 mb-3">
                                        <div className="flex align-items-center gap-3 mb-3">
                                            <div className="flex align-items-center justify-content-center bg-blue-100 border-round"
                                                 style={{width: '3rem', height: '3rem'}}>
                                                <i className="pi pi-id-card text-blue-500 text-2xl"></i>
                                            </div>
                                            <div>
                                                <h3 className="m-0 mb-1 text-900">{selectedType.applicant_name}</h3>
                                                <span className="text-600 text-sm">ID: {selectedType.id}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-3 pb-3 border-bottom-1 surface-border">
                                        <label className="text-600 text-sm mb-2 block font-semibold">Applicant Name</label>
                                        <div className="text-900 font-medium text-lg">{selectedType.applicant_name}</div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-3 pb-3 border-bottom-1 surface-border">
                                        <label className="text-600 text-sm mb-2 block font-semibold">Code</label>
                                        <div className="flex align-items-center gap-2">
                                            <i className="pi pi-tag text-500"></i>
                                            <span className="text-900 font-medium font-mono bg-blue-50 px-3 py-1 border-round">
                                                {selectedType.code}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-12">
                                    <div className="mb-2">
                                        <label className="text-600 text-sm mb-2 block font-semibold">Description</label>
                                        <div className="text-900">
                                            {selectedType.description || <span className="text-500 italic">No description provided</span>}
                                        </div>
                                    </div>
                                </div>

                                {selectedType.created_at && (
                                    <div className="col-12 md:col-6">
                                        <div className="mb-2">
                                            <label className="text-600 text-sm mb-2 block font-semibold">Created At</label>
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-calendar text-500"></i>
                                                <span className="text-900 text-sm">
                                                    {new Date(selectedType.created_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {selectedType.updated_at && (
                                    <div className="col-12 md:col-6">
                                        <div className="mb-2">
                                            <label className="text-600 text-sm mb-2 block font-semibold">Updated At</label>
                                            <div className="flex align-items-center gap-2">
                                                <i className="pi pi-clock text-500"></i>
                                                <span className="text-900 text-sm">
                                                    {new Date(selectedType.updated_at).toLocaleString()}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </Dialog>

                    {/* Edit Type Dialog */}
                    <Dialog
                        visible={editTypeDialog}
                        style={{ width: '500px' }}
                        header="Edit Applicant Type"
                        modal
                        className="p-fluid"
                        footer={editFooter}
                        onHide={() => setEditTypeDialog(false)}
                    >
                        {selectedType && (
                            <>
                                <div className="field">
                                    <label htmlFor="edit_applicant_name">Applicant Name *</label>
                                    <InputText
                                        id="edit_applicant_name"
                                        value={selectedType.applicant_name}
                                        onChange={(e) =>
                                            setSelectedType({ ...selectedType, applicant_name: e.target.value })
                                        }
                                        placeholder="Enter applicant name"
                                    />
                                </div>
                                <div className="field">
                                    <label htmlFor="edit_code">Code *</label>
                                    <InputText
                                        id="edit_code"
                                        value={selectedType.code}
                                        onChange={(e) =>
                                            setSelectedType({ ...selectedType, code: e.target.value })
                                        }
                                        placeholder="Enter code"
                                    />
                                </div>
                                <div className="field">
                                    <label htmlFor="edit_description">Description</label>
                                    <InputText
                                        id="edit_description"
                                        value={selectedType.description || ''}
                                        onChange={(e) =>
                                            setSelectedType({ ...selectedType, description: e.target.value })
                                        }
                                        placeholder="Enter description (optional)"
                                    />
                                </div>
                            </>
                        )}
                    </Dialog>
                </div>
            </div>
        </div>
    );
}